import { cn } from '#lib/utils'

/**
 * The cover of a residence: its first photo, or an illustration when it has
 * none. The illustration comes in four palettes, picked from the residence id
 * so a residence always shows the same one and a list does not look uniform.
 */
function ResidenceCover({
	image,
	seed,
	alt,
	wide,
	loading = 'lazy',
	className
}: {
	image?: {
		url: string
		width?: number | null
		height?: number | null
	} | null
	/** Stable id of the residence: it picks the placeholder palette. */
	seed: string
	alt: string
	/** The box is wider than 4:3: the illustration widens its landscape instead of cutting the building. */
	wide?: boolean
	/** `eager` for a photograph on the first screen, which must not wait to be scrolled to. */
	loading?: 'lazy' | 'eager'
	className?: string
}) {
	if (image) {
		return (
			<img
				alt={alt}
				className={cn('size-full object-cover', className)}
				decoding='async'
				height={image.height ?? undefined}
				loading={loading}
				src={image.url}
				width={image.width ?? undefined}
			/>
		)
	}
	return (
		<ResidencePlaceholder
			className={className}
			label={alt}
			seed={seed}
			wide={wide}
		/>
	)
}

/**
 * The illustration alone, for a residence without photos. Drawn 4:3; the wide
 * one is 8:3, with the hills stretched and the building in the middle, so a
 * banner keeps the whole building and a 4:3 box still shows the same scene.
 */
function ResidencePlaceholder({
	seed,
	label,
	wide,
	className
}: {
	seed: string
	/** What the illustration stands for, e.g. the name of the residence. */
	label: string
	wide?: boolean
	className?: string
}) {
	const palette = PALETTES[paletteIndex(seed)] ?? PALETTES[0]
	const centre = wide ? 'translate(200 0)' : undefined
	return (
		<svg
			aria-label={label}
			className={cn('size-full', className)}
			preserveAspectRatio='xMidYMid slice'
			role='img'
			viewBox={wide ? '0 0 800 300' : '0 0 400 300'}
			xmlns='http://www.w3.org/2000/svg'
		>
			<rect fill={palette.sky} height='300' width={wide ? 800 : 400} />
			<circle
				cx={palette.sunX}
				cy='70'
				fill={palette.accent}
				opacity='0.55'
				r='26'
				transform={centre}
			/>
			<g transform={wide ? 'scale(2 1)' : undefined}>
				<path
					d='M0 205 C70 165 130 175 200 190 C270 205 330 160 400 180 V300 H0 Z'
					fill={palette.far}
				/>
				<path
					d='M0 240 C80 215 150 232 230 228 C310 224 350 214 400 222 V300 H0 Z'
					fill={palette.near}
				/>
			</g>
			<g transform={centre}>
				{/* The building: two storeys, a flat roof with a cap, an arched door. */}
				<rect
					fill={palette.wall}
					height='112'
					rx='6'
					width='188'
					x='106'
					y='128'
				/>
				<rect
					fill={palette.roof}
					height='14'
					rx='4'
					width='204'
					x='98'
					y='118'
				/>
				<g fill={palette.window}>
					{[126, 170, 214, 258].flatMap((x) =>
						[142, 182].map((y) => (
							<rect
								height='24'
								key={`${x}-${y}`}
								rx='3'
								width='20'
								x={x}
								y={y}
							/>
						))
					)}
				</g>
				<path
					d='M186 240 V212 a14 14 0 0 1 28 0 V240 Z'
					fill={palette.roof}
				/>
				<g>
					<path
						d='M200 78 L238 94 L200 110 L162 94 Z'
						fill={palette.roof}
					/>
					<path
						d='M180 102 V114 Q200 124 220 114 V102 L200 110 Z'
						fill={palette.roof}
						opacity='0.85'
					/>
					<path
						d='M234 96 V116'
						stroke={palette.accent}
						strokeLinecap='round'
						strokeWidth='3'
					/>
					<circle cx='234' cy='119' fill={palette.accent} r='4' />
				</g>
				<g
					fill={palette.near}
					stroke={palette.roof}
					strokeOpacity='0.25'
					strokeWidth='2'
				>
					<circle cx='62' cy='222' r='26' />
					<circle cx='342' cy='226' r='22' />
				</g>
				<g stroke={palette.roof} strokeLinecap='round' strokeWidth='4'>
					<path d='M62 248 V232' />
					<path d='M342 248 V236' />
				</g>
			</g>
		</svg>
	)
}

const PALETTES = [
	{
		sky: '#E6ECF8',
		far: '#C7D4EE',
		near: '#A9BCE3',
		wall: '#FFFFFF',
		roof: '#2A3C86',
		window: '#7F97D4',
		accent: '#F2B63D',
		sunX: 320
	},
	{
		sky: '#FFF4DC',
		far: '#FBE3B0',
		near: '#F4CF84',
		wall: '#FFFDF7',
		roof: '#B8741A',
		window: '#E9B45A',
		accent: '#2A3C86',
		sunX: 76
	},
	{
		sky: '#E5F2EA',
		far: '#C5E1CF',
		near: '#A3CDB2',
		wall: '#FFFFFF',
		roof: '#2F6B4A',
		window: '#78B995',
		accent: '#F2B63D',
		sunX: 330
	},
	{
		sky: '#FBE9E3',
		far: '#F4CDBF',
		near: '#EBAF9C',
		wall: '#FFFDFB',
		roof: '#A8472E',
		window: '#E58F77',
		accent: '#2A3C86',
		sunX: 70
	}
] as const

const HASH_MODULUS = 2_147_483_647

/**
 * Which of the four palettes a residence gets: the same one every time. The
 * palette is read from the top of the hash, not its remainder, so ids that share
 * a long prefix, as ULIDs and page addresses do, still spread over the four.
 */
function paletteIndex(seed: string) {
	let hash = 0
	for (const char of seed) {
		hash = (hash * 31 + (char.codePointAt(0) ?? 0)) % HASH_MODULUS
	}
	return Math.floor((hash / HASH_MODULUS) * PALETTES.length)
}

export { ResidenceCover, ResidencePlaceholder }
